/**
 * Vendor profile card for Settings > My-Kitchen (Plan 19).
 *
 * Two stacked blocks:
 *
 *   1. **Self-service** — display name (placeholder), description, contact
 *      email (placeholder), contact phone, time zone. Chef edits freely;
 *      writes are debounced ~700ms and persisted via the REST PATCH.
 *
 *   2. **Managed by Ordo** — read-only legal name, EIN (masked), business
 *      address, served-locations list. Each row has a "Request a change"
 *      CTA that opens the Plan-27 RequestChangeModal pre-filled with the
 *      kitchen org id, field, and current value.
 *
 * Schema gap (per the build packet's "DO NOT modify Prisma schema" rule):
 *   - Organization is missing `displayName`, `contactEmail`, `legalName`,
 *     `ein`, `businessAddressId`, `servedLocations`. We surface
 *     "Not yet provisioned" badges so the workflow is still wired (the
 *     change-request CTA captures field + proposed value as freeform
 *     strings) and the schema delta is documented as a follow-up.
 */

import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { FormField } from '@ordo/ui/form-field'
import { Input } from '@ordo/ui/input'
import { Separator } from '@ordo/ui/separator'
import { toast } from '@ordo/ui/sonner'
import { Textarea } from '@ordo/ui/textarea'
import { Building2, Pencil } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { type KitchenOrganizationDetail, maskEin, useUpdateKitchenProfileMutation } from '@/api/kitchen-settings'

import { KitchenChangeRequestModal } from './kitchen-change-request-modal'

const DEBOUNCE_MS = 700

interface VendorProfileDraft {
  displayName: string
  contactEmail: string
  phoneNumber: string
  timeZone: string
  description: string
}

function deriveDraft(org: KitchenOrganizationDetail): VendorProfileDraft {
  return {
    displayName: org.displayName ?? org.name ?? '',
    contactEmail: org.contactEmail ?? '',
    phoneNumber: org.phoneNumber ?? '',
    timeZone: org.timeZone ?? '',
    description: org.description ?? '',
  }
}

export interface VendorProfileCardProps {
  organization: KitchenOrganizationDetail
}

interface PendingChangeRequest {
  field: string
  fieldLabel: string
  currentValue: string | null
}

export function VendorProfileCard({ organization }: VendorProfileCardProps) {
  const [draft, setDraft] = useState<VendorProfileDraft>(() => deriveDraft(organization))
  const [pending, setPending] = useState<PendingChangeRequest | null>(null)
  const orgRef = useRef(organization)
  const updateMutation = useUpdateKitchenProfileMutation()
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDraftRef = useRef<VendorProfileDraft | null>(null)

  // Re-seed when the upstream org row changes — but only when no edit is
  // pending, so an in-flight save doesn't clobber the user's typing.
  useEffect(() => {
    orgRef.current = organization
    if (pendingDraftRef.current === null) {
      setDraft(deriveDraft(organization))
    }
  }, [organization])

  const persist = useCallback(
    (next: VendorProfileDraft) => {
      pendingDraftRef.current = next
      updateMutation
        .mutateAsync({
          organizationId: orgRef.current.id,
          // Today only phoneNumber + timeZone + description hit the DB.
          // displayName + contactEmail are documented schema follow-ups;
          // we send them anyway so the REST surface accepts them once the
          // columns land (the server currently ignores them).
          phoneNumber: next.phoneNumber.trim() === '' ? null : next.phoneNumber,
          timeZone: next.timeZone.trim() === '' ? null : next.timeZone,
          description: next.description.trim() === '' ? null : next.description,
          displayName: next.displayName.trim() === '' ? null : next.displayName,
          contactEmail: next.contactEmail.trim() === '' ? null : next.contactEmail,
        })
        .then(() => {
          pendingDraftRef.current = null
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : 'Try again in a moment.'
          toast.error("Couldn't save kitchen profile", { description: message })
          pendingDraftRef.current = null
        })
    },
    [updateMutation],
  )

  const scheduledPersist = useCallback(
    (next: VendorProfileDraft) => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current)
      }
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null
        persist(next)
      }, DEBOUNCE_MS)
    },
    [persist],
  )

  // Flush the debounce on unmount so a final keystroke isn't dropped.
  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current)
        debounceTimer.current = null
      }
    }
  }, [])

  function updateField<K extends keyof VendorProfileDraft>(k: K, v: string) {
    setDraft((prev) => {
      const next = { ...prev, [k]: v }
      scheduledPersist(next)
      return next
    })
  }

  function openRequest(field: string, fieldLabel: string, currentValue: string | null) {
    setPending({ field, fieldLabel, currentValue })
  }

  const servedLocationSummary =
    organization.servedLocations.length === 0 ? null : `${organization.servedLocations.length} schools`

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" />
            Vendor profile
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            How this kitchen presents to schools and families. Self-service fields below; compliance + payout fields are
            managed by Ordo.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {/* --- Self-service block --- */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label="Display name">
              <Input
                value={draft.displayName}
                onChange={(e) => updateField('displayName', e.target.value)}
                placeholder={organization.name}
              />
            </FormField>
            <FormField label="Contact email">
              <Input
                type="email"
                value={draft.contactEmail}
                onChange={(e) => updateField('contactEmail', e.target.value)}
                placeholder="orders@kitchen.com"
              />
            </FormField>
            <FormField label="Contact phone">
              <Input
                value={draft.phoneNumber}
                onChange={(e) => updateField('phoneNumber', e.target.value)}
                placeholder="(555) 555-0100"
              />
            </FormField>
            <FormField label="Time zone">
              <Input
                value={draft.timeZone}
                onChange={(e) => updateField('timeZone', e.target.value)}
                placeholder="America/Los_Angeles"
              />
            </FormField>
            <FormField label="Description" className="md:col-span-2">
              <Textarea
                value={draft.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="What this kitchen specializes in — drives the family menu card subtext."
                rows={3}
              />
            </FormField>
          </div>

          <Separator />

          {/* --- Ordo-provisioned block --- */}
          <div className="flex flex-col gap-1">
            <h4 className="text-sm font-medium text-muted-foreground">Managed by Ordo</h4>
            <p className="text-xs text-muted-foreground">
              These fields drive AP payouts, 1099 reporting, and contractual relationships. Edits route through the Ordo
              review queue.
            </p>
            <div className="mt-3 flex flex-col">
              <ReadOnlyRow
                label="Business legal name"
                value={organization.legalName}
                onRequest={() => openRequest('legalName', 'Business legal name', organization.legalName)}
              />
              <ReadOnlyRow
                label="EIN"
                value={organization.ein ? maskEin(organization.ein) : null}
                onRequest={() => openRequest('ein', 'EIN', organization.ein)}
                helpText="US Employer Identification Number. Flows into Tipalti payout instructions + IRS 1099 records."
              />
              <ReadOnlyRow
                label="Business address"
                value={organization.businessAddress}
                onRequest={() => openRequest('businessAddress', 'Business address', organization.businessAddress)}
                helpText="Used for W-9 verification + payout records."
              />
              <ReadOnlyRow
                label="Served locations"
                value={servedLocationSummary}
                onRequest={() =>
                  openRequest(
                    'servedLocations',
                    'Served locations',
                    organization.servedLocations.map((l) => l.name).join(', ') || null,
                  )
                }
                helpText="Which schools this kitchen ships to. Cascades into SchoolMealProgram links on approval."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {pending ? (
        <KitchenChangeRequestModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setPending(null)
            }
          }}
          kitchenOrgId={organization.id}
          field={pending.field}
          fieldLabel={pending.fieldLabel}
          currentValue={pending.currentValue}
        />
      ) : null}
    </>
  )
}

function ReadOnlyRow({
  label,
  value,
  helpText,
  onRequest,
}: {
  label: string
  value: string | null
  helpText?: string
  onRequest: () => void
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b py-3 last:border-b-0">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        {value !== null && value !== '' ? (
          <span className="truncate text-sm font-medium">{value}</span>
        ) : (
          <Badge variant="outline" className="w-fit text-xs">
            Not yet provisioned
          </Badge>
        )}
        {helpText ? <span className="text-xs text-muted-foreground">{helpText}</span> : null}
      </div>
      <Button type="button" variant="ghost" size="sm" onClick={onRequest} className="shrink-0">
        <Pencil className="mr-1 h-3 w-3" />
        Request a change
      </Button>
    </div>
  )
}
